import { type ReactNode } from 'react';
import {
  ChoiceChip,
  Text,
  TextInput
} from 'stubben-edge-unified-frontend-react-components';
import type { SelectOptionProp } from '@ui/SelectField/types';
import { DatatableFilterSearchInput } from './DatatableFilterSearchInput';
import { DatatableFilterSelectInput } from './DatatableFilterSelectInput';
import { resolveDateInputMax } from './resolveDateInputMax';
import type {
  DataTableProviderFilterItem,
  DataTableProviderFilterStateValue
} from './types';

/** Stubben field `inputProps` / `selectProps` typings omit `data-testid`; tests need it on the native control. */
type NativePropsWithTestId = Record<string, unknown>;

export function getFilterDrawerFieldLabel(
  filter: DataTableProviderFilterItem
): string {
  if (filter.hideLabel) return filter.placeholder || filter.label;
  return filter.label;
}

export function getFilterTestIdentifier(filterKey: string): string {
  return `datatable-filter-${filterKey.replace(/[_.]/g, '-')}`;
}

export function getChoiceChipFilterTestIdentifier(
  filterKey: string,
  value: string | number
): string {
  const suffix =
    value === '' || value === undefined ? 'all' : String(value).toLowerCase();
  return `${getFilterTestIdentifier(filterKey)}-${suffix}`;
}

export function isChoiceChipSelected(
  currentValue: string,
  optionValue: string,
  multiSelect?: boolean
): boolean {
  if (optionValue === '') {
    return currentValue === '';
  }
  if (multiSelect) {
    return currentValue
      .split(',')
      .map((part) => part.trim())
      .filter(Boolean)
      .includes(optionValue);
  }
  return currentValue === optionValue;
}

export function getChoiceChipNextValue(
  currentValue: string,
  optionValue: string,
  optionsCount: number
): string {
  return optionsCount === 1 && currentValue === optionValue ? '' : optionValue;
}

/** Half-width in the 2-col drawer unless fullWidth/type defaults say otherwise. */
export function isFilterDrawerFieldFullWidth(
  filter: DataTableProviderFilterItem
): boolean {
  if (filter.fullWidth === true) return true;
  // Explicit false opts a `select` out of the legacy full-width default (CRM-606).
  if (filter.fullWidth === false) return false;
  if (filter.type === 'date_range') return true;
  if (filter.type === 'select') return true;
  if (filter.type === 'choice_chips' && !filter.inlineWithPrevious) return true;
  return false;
}

export function renderFilterDrawerGrid(props: {
  filtersList: DataTableProviderFilterItem[];
  filtersState: Map<string, DataTableProviderFilterStateValue>;
  handleFilterInputChange: (
    item: DataTableProviderFilterItem
  ) => (value: string | number | string[]) => void;
  handleFilterSelectChange: (
    item: DataTableProviderFilterItem
  ) => (
    value: string | number | string[],
    option?: SelectOptionProp
  ) => void;
  handleAutoCompleteChange: (
    item: DataTableProviderFilterItem
  ) => (
    value: string | number | string[],
    option?: SelectOptionProp
  ) => void;
  getFilterTestIdentifierFn?: (filterKey: string) => string;
}): ReactNode {
  const {
    filtersList,
    filtersState,
    handleFilterInputChange,
    handleFilterSelectChange,
    handleAutoCompleteChange,
    getFilterTestIdentifierFn = getFilterTestIdentifier
  } = props;

  // items-end: TextInput floating labels sit level with SearchInput boxes (CRM-606).
  return (
    <div className="grid w-full grid-cols-1 items-end gap-6 md:grid-cols-2">
      {filtersList.map((filter) => {
        const filterTestIdentifier = getFilterTestIdentifierFn(
          filter.filterKey
        );
        return (
          <div
            className={[
              isFilterDrawerFieldFullWidth(filter) ? 'md:col-span-2' : '',
              filter.alignEnd ? 'md:self-end' : ''
            ]
              .filter(Boolean)
              .join(' ')}
            key={filter.filterKey}
            data-testid={`${filterTestIdentifier}-wrapper`}
          >
            {filter.type === 'text' ? (
              <TextInput
                label={getFilterDrawerFieldLabel(filter)}
                value={
                  (filtersState?.get(filter.filterKey)?.value as string) || ''
                }
                disabled={filter.disabled || false}
                onChange={handleFilterInputChange(filter)}
                testIdentifier={filterTestIdentifier}
                inputProps={
                  {
                    'data-testid': filterTestIdentifier
                  } as NativePropsWithTestId as never
                }
              />
            ) : filter.type === 'number' ? (
              <TextInput
                type="number"
                label={getFilterDrawerFieldLabel(filter)}
                value={
                  (filtersState?.get(filter.filterKey)?.value as string) || ''
                }
                disabled={filter.disabled || false}
                onChange={handleFilterInputChange(filter)}
                testIdentifier={filterTestIdentifier}
                inputProps={
                  {
                    min:
                      (filter.moreThan &&
                        filtersState.get(filter.moreThan) &&
                        Number(filtersState.get(filter.moreThan)?.value)) ||
                      filter.minValue,
                    max:
                      (filter.lessThan &&
                        filtersState.get(filter.lessThan) &&
                        Number(filtersState.get(filter.lessThan)?.value)) ||
                      filter.maxValue,
                    'data-testid': filterTestIdentifier
                  } as NativePropsWithTestId as never
                }
              />
            ) : filter.type === 'select' ? (
              <DatatableFilterSelectInput
                filter={filter}
                label={getFilterDrawerFieldLabel(filter)}
                value={
                  (filtersState?.get(filter.filterKey)?.value as string) || ''
                }
                disabled={filter.disabled || false}
                testIdentifier={filterTestIdentifier}
                onChange={handleFilterSelectChange(filter)}
              />
            ) : filter.type === 'choice_chips' ? (
              <div className="flex flex-col gap-2">
                {!filter.hideLabel ? (
                  <Text size="sm" className="text-gray-600">
                    {getFilterDrawerFieldLabel(filter)}
                  </Text>
                ) : null}
                <div className="flex flex-wrap gap-2">
                  {(filter.selectOptions ?? []).map((option) => {
                    const optionValue = String(option.value ?? '');
                    const currentValue =
                      (filtersState?.get(filter.filterKey)?.value as string) ??
                      '';
                    return (
                      <ChoiceChip
                        key={`${filter.filterKey}-${optionValue || 'all'}`}
                        size="sm"
                        selected={isChoiceChipSelected(
                          currentValue,
                          optionValue,
                          filter.multiSelect
                        )}
                        disabled={filter.disabled || false}
                        onClick={() =>
                          handleFilterSelectChange(filter)(
                            getChoiceChipNextValue(
                              currentValue,
                              optionValue,
                              filter.selectOptions?.length ?? 0
                            ),
                            option
                          )
                        }
                        testIdentifier={getChoiceChipFilterTestIdentifier(
                          filter.filterKey,
                          optionValue
                        )}
                      >
                        {option.label}
                      </ChoiceChip>
                    );
                  })}
                </div>
              </div>
            ) : filter.type === 'autocomplete' ? (
              <DatatableFilterSearchInput
                filter={filter}
                filtersState={filtersState}
                label={getFilterDrawerFieldLabel(filter)}
                value={
                  (filtersState?.get(filter.filterKey)?.value as string) || ''
                }
                disabled={filter.disabled || false}
                testIdentifier={filterTestIdentifier}
                onChange={handleAutoCompleteChange(filter)}
              />
            ) : filter.type === 'date' ? (
              <TextInput
                type="date"
                label={getFilterDrawerFieldLabel(filter)}
                disabled={filter.disabled || false}
                value={
                  (filtersState?.get(filter.filterKey)?.value as string) || ''
                }
                onChange={handleFilterInputChange(filter)}
                testIdentifier={filterTestIdentifier}
                inputProps={
                  {
                    min:
                      (filter.moreThan &&
                        filtersState.get(filter.moreThan) &&
                        `${filtersState.get(filter.moreThan)?.value}`) ||
                      filter.minValue,
                    max: resolveDateInputMax({
                      rangeBound:
                        filter.lessThan &&
                        filtersState.get(filter.lessThan)?.value != null
                          ? `${filtersState.get(filter.lessThan)?.value}`
                          : null,
                      maxValue: filter.maxValue,
                      disableFuture: filter.disableFuture
                    }),
                    'data-testid': filterTestIdentifier
                  } as NativePropsWithTestId as never
                }
              />
            ) : filter.type === 'date_range' ? (
              <>
                {(() => {
                  const rangeValue = (filtersState?.get(filter.filterKey)
                    ?.value as string[]) || ['', ''];
                  return (
                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                      <TextInput
                        type="date"
                        label={`${filter.label} (From)`}
                        value={rangeValue[0] as string}
                        onChange={(value) => {
                          handleFilterInputChange(filter)([
                            value,
                            rangeValue[1]
                          ]);
                        }}
                        testIdentifier={`${filterTestIdentifier}-from`}
                        inputProps={
                          {
                            max: resolveDateInputMax({
                              rangeBound: rangeValue[1] || null,
                              disableFuture: filter.disableFuture
                            }),
                            'data-testid': `${filterTestIdentifier}-from`
                          } as NativePropsWithTestId as never
                        }
                      />
                      <TextInput
                        type="date"
                        label={`${filter.label} (To)`}
                        value={rangeValue[1] as string}
                        onChange={(value) => {
                          handleFilterInputChange(filter)([
                            rangeValue[0],
                            value
                          ]);
                        }}
                        testIdentifier={`${filterTestIdentifier}-to`}
                        inputProps={
                          {
                            min: rangeValue[0] || undefined,
                            max: resolveDateInputMax({
                              disableFuture: filter.disableFuture
                            }),
                            'data-testid': `${filterTestIdentifier}-to`
                          } as NativePropsWithTestId as never
                        }
                      />
                    </div>
                  );
                })()}
              </>
            ) : filter.type === 'time' ? (
              <TextInput
                type="time"
                label={getFilterDrawerFieldLabel(filter)}
                value={
                  (filtersState?.get(filter.filterKey)?.value as string) || ''
                }
                onChange={handleFilterInputChange(filter)}
                testIdentifier={filterTestIdentifier}
                inputProps={
                  {
                    min:
                      (filter.moreThan &&
                        filtersState.get(filter.moreThan) &&
                        `${filtersState.get(filter.moreThan)?.value}`) ||
                      filter.minValue,
                    max:
                      (filter.lessThan &&
                        filtersState.get(filter.lessThan) &&
                        `${filtersState.get(filter.lessThan)?.value}`) ||
                      filter.maxValue,
                    'data-testid': filterTestIdentifier
                  } as NativePropsWithTestId as never
                }
              />
            ) : (
              <></>
            )}
          </div>
        );
      })}
    </div>
  );
}
